import React, { useRef, useEffect, useState } from 'react'
import classNames from 'classnames'
import { isEnterKey, isTabKey, nullIfNaN } from '../../../utils';
import { createPortal } from 'react-dom';

type Renderer = JSX.Element | ((props?: any) => JSX.Element);

export interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement & HTMLTextAreaElement> {
  /**
   * Invoked if only numeric characters are allowed and the input value
   * has been parsed to a numeric value.
   * 
   * Can be debounced by setting the `debounce` prop.
   */
  onNumericValueChange?: (val: number) => void;

  /**
   * Can be debounced by setting the `debounce` prop.
   */
  onTextValueChange?: (value: string) => void;

  multiline?: boolean;
  nodeRef?: any;
  prefix?: string;
  suffix?: React.ReactNode;
  allow?: AllowChars;
  expandable?: boolean;
  rows?: number;
  maxRows?: number;
  renderStart?: Renderer;
  renderEnd?: Renderer;
  centerRenderedStart?: boolean;
  centerRenderedEnd?: boolean;

  /**
   * Renders an state indicating invalid input.
   */
  invalid?: boolean;

  testId?: string;

  /**
   * If true-ish then debounces `on*ValueChange` callbacks with the provided delay in milliseconds.
   * Set to `true` for default value of 650.
   */
  debounce?: boolean | boolean;

  allowPasswordManagerAutofill?: boolean;

  /**
   * Whether to focus on the next tab index. Requires own tabIndex to be set.
   */
  focusNextOnEnter?: boolean;

  /**
   * Formats the value, visible when blurred.
   * 
   * If `true`, and `allow` is `numeric`, then the numeric value is formatted.
   * 
   * @default true
   */
  formatBlurredValue?: true | false | ((value: string | string[] | number) => string);

  /**
   * Whether to format the value, if blurred, and if the value evaluates to false.
   * 
   * @default true
   */
  formatBlurredEmptyValue?: boolean;
}

function restProps(props: TextInputProps) {
  let rest: any = { ...props }

  rest['data-testid'] = props.testId;

  if (!props.allowPasswordManagerAutofill) {
    rest['data-lpignore'] = "true"; // Disable Lastpass autofill.
  }

  return rest
}

function areCharsAllowed(e: React.KeyboardEvent, allow: AllowChars) {
  const { keyCode } = e

  if (e.ctrlKey || e.metaKey) {
    return true;
  }

  //  check special keys
  if ([8, 9, 13, 37, 38, 39, 40].indexOf(keyCode) != -1) {
    return true
  }

  switch (allow) {
    case 'numeric':
    case 'numericSpecial':
      if (e.shiftKey) {
        return false;
      }

      //  check decimal point
      if ([188, 190].indexOf(keyCode) != -1) {
        return true
      }

      //  +, - and space
      if (allow == 'numericSpecial' && [187, 189, 32].indexOf(keyCode) != -1) {
        return true
      }

      //  check digits
      if (!((keyCode >= 48 && keyCode <= 57) || (keyCode >= 96 && keyCode <= 105))) {
        return false
      }
  }

  return true
}

export type AllowChars = 'all' | 'numeric' | 'numericSpecial';

/**
 * Extended text input.
 */
export const TextInput: React.FC<TextInputProps> = ({
  allow = 'all',
  nodeRef,
  expandable,
  renderStart,
  renderEnd,
  prefix,
  suffix,
  focusNextOnEnter,
  invalid,
  maxRows,
  debounce,
  ...props
}) => {
  expandable = expandable || maxRows != null;

  const lineHeight = 22;

  const isNumeric = allow === "numeric";
  let debounceDelay: number = debounce as any || null;

  if (debounceDelay as any === true) {
    debounceDelay = 650;
  }

  if (props.type == 'number') {
    throw new Error('use `allow="numeric"` instead of `type="number"`');
  }

  let RenderAsideComponentBefore = renderStart;
  let RenderAsideComponentAfter = renderEnd;

  if (!RenderAsideComponentBefore && prefix) {
    RenderAsideComponentBefore = () => (
      <p className="prefix">{prefix}</p>
    );
  }

  if (!RenderAsideComponentAfter && suffix) {
    RenderAsideComponentAfter = () => (
      <p className="suffix">{suffix}</p>
    );
  }

  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const lineMeasureRef = useRef<HTMLDivElement>(null);
  const onNumericValueChangeTimeout = useRef(null);
  const onTextValueChangeTimeout = useRef(null);

  //  used for styling purposes (parent container)
  const [focused, setFocused] = React.useState(false);

  function getDefaultRows() {
    const valueRowCount = measureRows(inputRef.current);
    const minimumRows = props.rows ? Math.max(props.rows, valueRowCount) : valueRowCount;

    return maxRows ? Math.min(minimumRows, maxRows) : minimumRows;
  }

  const [rows, setRows] = useState(getDefaultRows());

  //  needed if debounced.
  const [textValue, setTextValue] = React.useState("");

  useEffect(() => {
    const { value } = props;
    setTextValue(String(value || ''));
    setRows(getDefaultRows());
  }, [props.value])

  function handleFocusOnNextInput(e: React.KeyboardEvent) {
    if (!focusNextOnEnter || (!isEnterKey(e) && !isTabKey(e))) {
      return false
    }

    let { tabIndex } = props
    if (typeof tabIndex !== "number") {
      return false
    }

    let focusedElement = document.querySelector(`[tabindex="${tabIndex + 1}"]`) as HTMLElement
    if (focusedElement) {
      focusedElement.focus()
      e.preventDefault()
      return true
    }

    return false
  }

  function handleOnTextValueChange(value: string) {
    setTextValue(value);

    if (typeof props.onTextValueChange !== 'function') {
      return;
    }

    if (debounceDelay) {
      clearTimeout(onTextValueChangeTimeout.current);
      onTextValueChangeTimeout.current = setTimeout(props.onTextValueChange.bind(null, value),
        debounceDelay);
    } else {
      props.onTextValueChange(value);
    }
  }

  function handleOnNumericValueChange(value: number) {
    if (typeof props.onNumericValueChange !== 'function') {
      return;
    }

    if (debounceDelay) {
      clearTimeout(onNumericValueChangeTimeout.current);
      onNumericValueChangeTimeout.current = setTimeout(props.onNumericValueChange.bind(null, value),
        debounceDelay);
    } else {
      props.onNumericValueChange(value);
    }
  }

  /**
   * Measures the height of the current value in relation to `lineHeight` to determine the row count.
   * @returns Row count.
   */
  function measureRows(element: HTMLInputElement | HTMLTextAreaElement): number {
    const value = element?.value || String(props.value);

    if (!lineMeasureRef.current) {
      const hardRows = (value.match(/\n/g) || []).length + 1;
      return hardRows;
    }

    const bounds = element.getBoundingClientRect();

    const measurer = lineMeasureRef.current;
    measurer.style.width = bounds.width + 'px';
    measurer.innerText = value.replace(/\n$/, '\n|'); //  Make sure any trailing new line accumulates space

    const measuredRows = Math.ceil(measurer.getBoundingClientRect().height / lineHeight);
    return measuredRows;
  }

  const defaultMaxLength = props.multiline ? 1024 : 240;
  const maxLength = [undefined, null].indexOf(props.maxLength) !== -1 ? defaultMaxLength : props.maxLength;

  let renderedValue = textValue;
  if (renderedValue === undefined) {
    renderedValue = null;
  }
  renderedValue = !focused ? formatBlurredValue({ ...props, value: renderedValue }) : renderedValue;

  const rowMeasurer = expandable ? createPortal((
    <p
      style={{ lineHeight: lineHeight + 'px' }}
      ref={e => lineMeasureRef.current = e}>
    </p>
  ), document.getElementById('helper-root')) : null;

  return (
    <div
      className={classNames({
        "text-input-container input-like": true,
        "focus": focused,
        "disabled": props.disabled,
        "invalid": invalid
      })}
      style={
        props.multiline && RenderAsideComponentAfter == null && RenderAsideComponentBefore == null ? {
          //  use column direction to have input/textarea fill parent on iOS 
          flexDirection: 'column'
        } : null}>

      {
        RenderAsideComponentBefore ? (
          <div className={classNames({ "text-input-aside": true, "flex-center": props.centerRenderedStart })}>
            {typeof RenderAsideComponentBefore == 'function' ? (
              <RenderAsideComponentBefore />
            ) : (RenderAsideComponentBefore)}
          </div>
        ) : null
      }

      {
        props.multiline ? (
          //  @ts-ignore
          <textarea
            ref={e => {
              inputRef.current = e;
              nodeRef?.(e);
            }}
            {...restProps(props)}
            rows={expandable ? rows : undefined}
            maxLength={maxLength}
            onFocus={e => {
              props.onFocus?.(e as any);
              setFocused(true)
            }}
            onBlur={e => {
              props.onBlur?.(e as any);
              setFocused(false)
            }}
            onKeyDown={e => {
              if (isEnterKey(e)) {
                e.stopPropagation();
              }

              handleFocusOnNextInput(e)
              props.onKeyDown?.(e as any);
              if (!areCharsAllowed(e, allow)) {
                //  non-allowed chars
                e.preventDefault()
                return true;
              }
            }}
            onKeyUp={e => {
              if (isEnterKey(e)) {
                //  Prevents a save-like action on enter onKeyUp. 
                e.stopPropagation();
              }
            }}
            onChange={(e: any) => {
              let val: string = e.currentTarget.value

              if (!expandable) {
                props.onChange?.(e);
                handleOnTextValueChange(e.currentTarget.value);
                return
              }
              const { currentTarget } = e;

              let nextRows = measureRows(currentTarget);

              if (props.rows) {
                nextRows = Math.max(nextRows, props.rows)
              }

              if (maxRows > 0) {
                nextRows = Math.min(nextRows, maxRows)
              }

              if (rows !== nextRows) {
                setRows(nextRows);
              }

              const { selectionStart, selectionEnd } = currentTarget

              props.onChange?.(e);
              handleOnTextValueChange(e.currentTarget.value);

              setTimeout(() => {
                currentTarget.selectionStart = selectionStart;
                currentTarget.selectionEnd = selectionEnd;
              }, 0);
            }} />
        ) : (
            <input
              type="text"
              ref={e => {
                inputRef.current = e;
                nodeRef?.(e);
              }}
              {...restProps(props)}
              value={renderedValue}
              onChange={e => {
                let val: string = e.currentTarget.value
                const { currentTarget } = e;

                if (isNumeric) {
                  val = clampNumericValue(val, props.min as number, props.max as number);

                  handleOnNumericValueChange(nullIfNaN(
                    parseFloat(
                      val.replace(/\,/, '.')
                    ))
                  );
                }

                handleOnTextValueChange(val);
                const { selectionStart, selectionEnd } = currentTarget

                props.onChange?.(e as any);

                setTimeout(() => {
                  try {
                    currentTarget.selectionStart = selectionStart;
                    currentTarget.selectionEnd = selectionEnd;
                  } catch (e) {
                    //  not supported on some types
                  }
                }, 0);
              }}
              maxLength={maxLength}
              onFocus={e => {
                props.onFocus?.(e as any);
                setFocused(true)
              }}
              onBlur={e => {
                props.onBlur?.(e as any);
                setFocused(false)

                if (isNumeric && !renderedValue && !debounceDelay) {
                  setTextValue(props.value as string);
                }
              }}
              onKeyDown={e => {
                handleFocusOnNextInput(e)
                props.onKeyDown?.(e as any);

                const pass = areCharsAllowed(e, allow);

                if (!pass) {
                  //  non-allowed chars
                  e.preventDefault()
                  return true;
                }
              }}
            />
          )
      }

      {
        RenderAsideComponentAfter ? (
          <div className={classNames({ "text-input-aside": true, "flex-center": props.centerRenderedEnd })}>
            {typeof RenderAsideComponentAfter == 'function' ? (
              <RenderAsideComponentAfter />
            ) : (RenderAsideComponentAfter)}
          </div>
        ) : null
      }

      {rowMeasurer}
    </div>
  )
}

/**
 * Returns the value to render while the input is blurred.
 * @param props Props to derive from. You may override the `value`.
 * @returns Formatted string to render in a blurred state.
 */
export function formatBlurredValue(props: TextInputProps) {
  const { value } = props;

  let { formatBlurredValue } = props;
  if (formatBlurredValue === true && props.allow === 'numeric') {
    formatBlurredValue = formatNumeric;
  }

  const shouldFormatBlurredValue = (props.formatBlurredEmptyValue ? true : !!props.value)
    && typeof formatBlurredValue === "function";

  if (!shouldFormatBlurredValue) {
    return value;
  }

  return (formatBlurredValue as any)(value);
}

export function formatNumeric(value: string | string[] | number) {
  return isNaN(value as any) ? '0' : Number(value as string)
    .toLocaleString('sv', { maximumFractionDigits: 2 });
}

/**
 * Clamps a value within `min` and `max` bounds if they are set.
 * @param value The input's value.
 * @returns The clamped value.
 */
function clampNumericValue(value: string, min?: number, max?: number): string {
  const valueAsNumeric = Number(value);

  if (typeof max === "number" && valueAsNumeric > max) {
    return String(max);
  }

  if (typeof min === "number" && valueAsNumeric < min) {
    return String(min);
  }

  return value;
}